import { Block } from 'framework7-react';
import { useT } from '@/i18n';

// ── Lead-time diagram ─────────────────────────────────────────────────────────
// The two timing fields do different jobs, so each gets its own little timeline:
//   • Min notice  → the booking cutoff: how soon after ordering the first
//                    bookable slot is (now + notice).
//   • Kitchen prep → not an order cutoff. It's the ASAP wait, and for a chosen
//                    slot it's how early the kitchen must start.
// Driven live off the editor draft. Pure CSS / F7 theme vars - no chart lib.
// Shared by the default-capacity editor and the special-hours editor.

function fmtClock(totalMin: number): string {
  const m = ((totalMin % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

// One stretch of time on a leg. A leg is drawn as a SINGLE bar built from these
// back-to-back, so prep vs drive is one continuous span the eye can compare —
// two stacked bars hide that ratio, because each is scaled independently.
interface Segment {
  minutes: number;
  /** Where this segment ends: "Ready", "Delivered", … */
  label: string;
  /** Clock time at that point (may be a range for the final one). */
  time: string;
  bg: string;
  lineColor: string;
}

function TimelineLeg({
  leftLabel,
  leftTime,
  segments,
  scale,
}: {
  leftLabel: string;
  leftTime: string;
  segments: Segment[];
  scale: number;
}) {
  const sub = 'var(--f7-list-item-subtitle-text-color)';
  // Bar length is the duration as a fraction of the shared scale, so legs are
  // directly comparable. Left inset (START_PCT) leaves a little track before
  // the bar; whatever track remains after the last marker reads as buffer time.
  const START_PCT = 4;
  const SPAN = 100 - START_PCT * 2;

  // Cumulative geometry: each segment's end is where the next one begins.
  let cumulative = 0;
  const marks = segments.map((seg) => {
    const from = cumulative;
    cumulative += seg.minutes;
    const pct = (m: number) => START_PCT + Math.min(1, scale > 0 ? m / scale : 1) * SPAN;
    // Floor the first segment's width so a tiny prep time is still a visible
    // block rather than a hairline.
    return { seg, startPct: pct(from), endPct: Math.max(pct(cumulative), START_PCT + 6) };
  });

  // Labels are ~64px wide; below that gap they'd overlap, so an interior label
  // is dropped rather than rendered on top of its neighbour. The colour split
  // still shows the boundary, so nothing silently disappears.
  const MIN_GAP_PCT = 17;
  const positions = [START_PCT, ...marks.map((m) => m.endPct)];
  const visible = marks.map((m, i) => {
    const isLast = i === marks.length - 1;
    if (isLast) return true; // the leg's outcome always shows
    const prev = positions[i];
    const next = positions[i + 2];
    return m.endPct - prev >= MIN_GAP_PCT && next - m.endPct >= MIN_GAP_PCT;
  });

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Track + segmented duration bar + a marker per boundary. */}
      <div style={{ position: 'relative', height: 30 }}>
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 7,
            bottom: 7,
            background: 'rgba(120,120,128,0.12)',
            borderRadius: 5,
          }}
        />
        {marks.map(({ seg, startPct, endPct }, i) => (
          <div
            key={seg.label}
            style={{
              position: 'absolute',
              left: `${startPct}%`,
              width: `${endPct - startPct}%`,
              top: 4,
              bottom: 4,
              background: seg.bg,
              // Round only the outer ends so adjacent segments read as one bar.
              borderTopLeftRadius: i === 0 ? 5 : 0,
              borderBottomLeftRadius: i === 0 ? 5 : 0,
              borderTopRightRadius: i === marks.length - 1 ? 5 : 0,
              borderBottomRightRadius: i === marks.length - 1 ? 5 : 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {seg.minutes} min
          </div>
        ))}
        {/* Start marker. */}
        <div
          style={{
            position: 'absolute',
            left: `${START_PCT}%`,
            top: -1,
            bottom: -1,
            borderLeft: `2px solid ${sub}`,
          }}
        />
        {marks.map(({ seg, endPct }) => (
          <div
            key={seg.label}
            style={{
              position: 'absolute',
              left: `${endPct}%`,
              top: -3,
              bottom: -3,
              borderLeft: `2px solid ${seg.lineColor}`,
            }}
          />
        ))}
      </div>

      {/* Labels sit BELOW their markers so they can never overflow the row. */}
      <div style={{ position: 'relative', height: 32, marginTop: 3 }}>
        <div style={{ position: 'absolute', left: `${START_PCT}%` }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: sub, whiteSpace: 'nowrap' }}>
            {leftLabel}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{leftTime}</div>
        </div>
        {marks.map(({ seg, endPct }, i) => {
          if (!visible[i]) return null;
          // Keep the label inside the box: hug left near the start, right near
          // the end, centred in between.
          const shift = endPct > 82 ? '-100%' : endPct < 18 ? '0%' : '-50%';
          return (
            <div
              key={seg.label}
              style={{
                position: 'absolute',
                left: `${endPct}%`,
                transform: `translateX(${shift})`,
                textAlign: shift === '-100%' ? 'right' : 'left',
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 600, color: sub, whiteSpace: 'nowrap' }}>
                {seg.label}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>{seg.time}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The delivery leg on top of kitchen prep, for the DELIVERY channel only.
 *
 * "Ready" is when the food leaves the kitchen — for delivery that is NOT when
 * the guest gets it, and an owner tuning prep time against a ready-only preview
 * will consistently under-promise. Static zones each carry their own time, so
 * the preview shows the spread; ROUTE mode is computed per address at checkout
 * and has no single number to show.
 */
export type DeliveryLeg =
  | { kind: 'static'; minMinutes: number; maxMinutes: number }
  | { kind: 'live' }
  | null;

export function LeadTimeDiagram({
  prepMinutes,
  noticeMinutes,
  openingBufferMinutes,
  openingMinutes = null,
  delivery = null,
}: {
  prepMinutes: number;
  noticeMinutes: number;
  openingBufferMinutes: number;
  openingMinutes?: number | null;
  delivery?: DeliveryLeg;
}) {
  const lt = useT().orderSetup.leadTime;
  const NOW = 12 * 60; // sample "order placed" time
  const SLOT = 19 * 60; // sample future slot a customer picks for a pre-order
  const OPEN = openingMinutes ?? 17 * 60;
  const deliveryMax = delivery?.kind === 'static' ? delivery.maxMinutes : 0;
  // Null when every zone promises the same time — then there's one number to
  // show instead of a pointless "45–45" range.
  const deliveryRange: [number, number] | null =
    delivery?.kind === 'static' && delivery.minMinutes !== delivery.maxMinutes
      ? [delivery.minMinutes, delivery.maxMinutes]
      : null;
  // Shared scale so both scenarios stay length-comparable. Measured against the
  // longest WHOLE leg (prep + drive), not its parts — scaling to a part would
  // push the combined bar past the end of its track.
  const scale = Math.max(prepMinutes + deliveryMax, noticeMinutes, openingBufferMinutes, 1);
  const accent = 'var(--f7-theme-color)';
  const accentBg = 'var(--f7-theme-color-tint, rgba(10,132,255,0.16))';
  const amber = '#FF9F0A';
  const amberBg = 'rgba(255,159,10,0.16)';
  const green = '#34C759';
  const greenBg = 'rgba(52,199,89,0.16)';
  const sub = 'var(--f7-list-item-subtitle-text-color)';

  return (
    <Block strongIos strongMd insetIos insetMd style={{ paddingTop: 14, paddingBottom: 14 }}>
      {/* ── Order now (ASAP): ready = now + kitchen prep ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: amber, marginBottom: 6 }}>
        {lt.orderNow}
      </div>
      {/* A zero prep time still has a drive to show — the guest waits for it
          either way, so the leg is drawn whenever EITHER part is non-zero. */}
      {prepMinutes > 0 || deliveryMax > 0 ? (
        <>
          {/* Prep then the drive, in the order the guest lives it. One bar, two
              segments — "ready" is the number owners mistake for "delivered", so
              it stays visible as the boundary rather than becoming its own row. */}
          <TimelineLeg
            leftLabel={lt.orderLabel}
            leftTime={fmtClock(NOW)}
            scale={scale}
            segments={[
              ...(prepMinutes > 0
                ? [
                    {
                      minutes: prepMinutes,
                      label: lt.readyLabel,
                      time: fmtClock(NOW + prepMinutes),
                      bg: amberBg,
                      lineColor: amber,
                    },
                  ]
                : []),
              ...(deliveryMax > 0
                ? [
                    {
                      minutes: deliveryMax,
                      label: lt.deliveredLabel,
                      time: deliveryRange
                        ? `${fmtClock(NOW + prepMinutes + deliveryRange[0])}–${fmtClock(
                            NOW + prepMinutes + deliveryRange[1],
                          )}`
                        : fmtClock(NOW + prepMinutes + deliveryMax),
                      bg: greenBg,
                      lineColor: green,
                    },
                  ]
                : []),
            ]}
          />
          <div style={{ fontSize: 12, marginTop: 8, color: sub }}>
            {deliveryMax > 0
              ? deliveryRange
                ? lt.deliveryRangeDescription(deliveryRange[0], deliveryRange[1])
                : lt.deliveryDescription(deliveryMax)
              : lt.readyDescription}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: sub }}>{lt.noPrepBuffer}</div>
      )}
      {delivery?.kind === 'live' && (
        <div style={{ fontSize: 12, marginTop: 8, color: sub }}>{lt.deliveryLiveDescription}</div>
      )}

      <div style={{ height: 1, background: 'rgba(120,120,128,0.18)', margin: '14px 0' }} />

      {/* ── Order for later (pre-order): min notice gates the slot; prep just
          decides when the kitchen starts before that slot. ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: accent, marginBottom: 6 }}>
        {lt.orderLater}
      </div>
      {noticeMinutes > 0 ? (
        <>
          <TimelineLeg
            leftLabel={lt.orderLabel}
            leftTime={fmtClock(NOW)}
            scale={scale}
            segments={[
              {
                minutes: noticeMinutes,
                label: lt.earliestSlot,
                time: fmtClock(NOW + noticeMinutes),
                bg: accentBg,
                lineColor: accent,
              },
            ]}
          />
          <div style={{ fontSize: 12, marginTop: 8, color: sub }}>
            {lt.minNoticeDescription(fmtClock(NOW + noticeMinutes))}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: sub }}>{lt.noNotice}</div>
      )}

      <div style={{ marginTop: 12 }}>
        {openingBufferMinutes > 0 ? (
          <>
            <TimelineLeg
              leftLabel={lt.opensLabel}
              leftTime={fmtClock(OPEN)}
              scale={scale}
              segments={[
                {
                  minutes: openingBufferMinutes,
                  label: lt.firstSlotLabel,
                  time: fmtClock(OPEN + openingBufferMinutes),
                  bg: accentBg,
                  lineColor: accent,
                },
              ]}
            />
            <div style={{ fontSize: 12, marginTop: 8, color: sub }}>
              {lt.openingBufferDescription(fmtClock(OPEN), fmtClock(OPEN + openingBufferMinutes))}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: sub }}>{lt.noOpeningBuffer}</div>
        )}
      </div>

      {(prepMinutes > 0 || deliveryMax > 0) && (
        <div style={{ marginTop: 12 }}>
          {/* A pre-ordered slot is the HANDOFF instant, so timing runs backwards
              from it (OrderTimingService: driverLeaveAt = slot − travel,
              prepStartAt = driverLeaveAt − prep). For delivery the drive
              therefore doesn't push the guest's time later — it pulls the
              kitchen earlier, the opposite of the ASAP case above. Worst-case
              (furthest zone) times, because that's the start the kitchen has to
              plan against. */}
          <TimelineLeg
            leftLabel={lt.kitchenStarts}
            leftTime={fmtClock(SLOT - deliveryMax - prepMinutes)}
            scale={scale}
            segments={[
              ...(prepMinutes > 0
                ? [
                    {
                      minutes: prepMinutes,
                      label: deliveryMax > 0 ? lt.driverLeaves : lt.readySlot,
                      time: fmtClock(SLOT - deliveryMax),
                      bg: amberBg,
                      lineColor: amber,
                    },
                  ]
                : []),
              ...(deliveryMax > 0
                ? [
                    {
                      minutes: deliveryMax,
                      label: lt.deliveredSlot,
                      time: fmtClock(SLOT),
                      bg: greenBg,
                      lineColor: green,
                    },
                  ]
                : []),
            ]}
          />
          <div style={{ fontSize: 12, marginTop: 8, color: sub }}>
            {deliveryMax > 0
              ? lt.kitchenStartsDeliveryDescription(
                  fmtClock(SLOT),
                  fmtClock(SLOT - deliveryMax - prepMinutes),
                )
              : lt.kitchenStartsDescription(fmtClock(SLOT), fmtClock(SLOT - prepMinutes))}
          </div>
          {delivery?.kind === 'live' && (
            <div style={{ fontSize: 12, marginTop: 6, color: sub }}>
              {lt.kitchenStartsDeliveryLive}
            </div>
          )}
        </div>
      )}
    </Block>
  );
}
